'use client';
import React, { useEffect } from 'react';
import Image from 'next/image';
import { EmblaOptionsType } from 'embla-carousel';
import { useDotButton } from './dotButton';
import useEmblaCarousel from 'embla-carousel-react';
import './embla.css';
import { NavButton, usePrevNextButtons } from './navButtons';

type PropType = {
  children: React.ReactNode;
  options?: EmblaOptionsType;
  carouselPadding?: string;
  slideHeight?: string;
  square?: boolean;
  slideSpacing?: string;
  slideSize?: string;
  showDots?: boolean;
  showDefaultButtons?: boolean;
  showTopButtons?: boolean;
  buttonClassName?: string;
};

export const Carousel: React.FC<PropType> = ({
  children,
  options,
  slideHeight = '375px',
  square = false,
  carouselPadding = '16px',
  slideSpacing = '16px',
  slideSize = '82%',
  showDots = true,
  showDefaultButtons = false,
  showTopButtons = false,
  buttonClassName = '',
}) => {
  const [emblaRef, emblaApi] = useEmblaCarousel(options);
  const { selectedIndex, scrollSnaps } = useDotButton(emblaApi);

  useEffect(() => {});

  const { prevBtnDisabled, nextBtnDisabled, onPrevButtonClick, onNextButtonClick } = usePrevNextButtons(emblaApi);

  const isRTL = options?.direction === 'rtl';

  return (
    <section
      className='embla relative'
      style={
        {
          '--carousel-padding': carouselPadding,
          '--slide-height': square ? 'auto' : slideHeight,
          '--slide-aspect': square ? '1/1' : 'auto',
          '--slide-spacing': slideSpacing,
          '--slide-size': slideSize,
        } as React.CSSProperties
      }
    >
      {showDefaultButtons && (
        <>
          <NavButton
            onClick={isRTL ? onNextButtonClick : onPrevButtonClick}
            disabled={isRTL ? nextBtnDisabled : prevBtnDisabled}
            style={{ height: slideHeight, width: '32px', position: 'absolute', left: '0px', top: '0px', zIndex: '20' }}
            icon={<Image src='/assets/icons/arrow-left.svg' alt='' height={20} width={20} />}
            disabledIcon={<Image src='/assets/icons/arrow-left-disabled.svg' alt='' height={20} width={20} />}
          />
          <NavButton
            onClick={isRTL ? onPrevButtonClick : onNextButtonClick}
            disabled={isRTL ? prevBtnDisabled : nextBtnDisabled}
            style={{ height: slideHeight, width: '32px', position: 'absolute', right: '0px', top: '0px', zIndex: '20' }}
            icon={<Image src='/assets/icons/arrow-right.svg' alt='' height={20} width={20} />}
            disabledIcon={<Image src='/assets/icons/arrow-right-disabled.svg' alt='' height={20} width={20} />}
          />
        </>
      )}

      {showTopButtons && (
        <>
          <NavButton
            className={buttonClassName}
            onClick={onPrevButtonClick}
            disabled={prevBtnDisabled}
            style={{ height: '40px', width: '40px', position: 'absolute', right: '50px', top: '-100px', zIndex: '20' }}
            icon={<Image src='/assets/icons/arrow-left.svg' alt='' height={20} width={20} />}
            disabledIcon={<Image src='/assets/icons/arrow-left-disabled.svg' alt='' height={20} width={20} />}
          />
          <NavButton
            className={buttonClassName}
            onClick={onNextButtonClick}
            disabled={nextBtnDisabled}
            style={{ height: '40px', width: '40px', position: 'absolute', right: '0px', top: '-100px', zIndex: '20' }}
            icon={<Image src='/assets/icons/arrow-right.svg' alt='' height={20} width={20} />}
            disabledIcon={<Image src='/assets/icons/arrow-right-disabled.svg' alt='' height={20} width={20} />}
          />
        </>
      )}

      <div className='embla__viewport' ref={emblaRef}>
        <div className='embla__container'>
          {React.Children.map(children, (child, index) => (
            <div className='embla__slide' key={index}>
              <div className='embla__slide__container'>{child}</div>
            </div>
          ))}
        </div>
      </div>

      {showDots && scrollSnaps.length > 1 && (
        <div className='absolute bottom-[12px] flex w-full justify-center'>
          <div className='w-fit flex items-center text-white rounded-[12px] text-[14px] px-[12px] h-[28px] bg-overlay-medium'>
            {selectedIndex + 1}/{scrollSnaps.length}
          </div>
        </div>
      )}
    </section>
  );
};
